import React, { useState, useEffect } from 'react';
import ProtectedLayout from '../components/ProtectedLayout';
import api from '../api';
import { toast } from 'sonner';

function Toggle({ active, onChange }) {
  return (
    <button
      onClick={e => { e.stopPropagation(); onChange(); }}
      style={{
        width: '44px', height: '24px', borderRadius: '999px',
        background: active ? '#EFF6FF' : '#F1F5F9',
        border: `1px solid ${active ? '#2D87F0' : '#DDE7F1'}`,
        position: 'relative', cursor: 'pointer', flexShrink: 0,
        padding: 0, overflow: 'hidden',
        transition: 'all 200ms',
      }}
    >
      <span style={{
        position: 'absolute',
        top: '50%',
        left: active ? 'calc(100% - 19px)' : '3px',
        transform: 'translateY(-50%)',
        width: '16px', height: '16px', borderRadius: '50%',
        background: active ? '#2D87F0' : '#94A3B8',
        transition: 'left 200ms, background 200ms',
      }} />
    </button>
  );
}

export default function EnginesPage() {
  const [engineGroups, setEngineGroups] = useState([]);
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    fetchEngines();
  }, []);

  const fetchEngines = async () => {
    try {
      const res = await api.get('/engines');
      setEngineGroups(res.data.engine_groups || []);
      setBrand(res.data.brand);
      // Auto-expand sections that are active
      const expanded = {};
      res.data.engine_groups.forEach(group => {
        group.engines.forEach(engine => {
          engine.sections.forEach(section => {
            expanded[section.id] = section.expanded !== false;
          });
        });
      });
      setExpandedSections(expanded);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const toggleEngine = (groupIdx, engineIdx) => {
    setEngineGroups(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated[groupIdx].engines[engineIdx].active = !updated[groupIdx].engines[engineIdx].active;
      return updated;
    });
  };

  const toggleCompany = (groupIdx, engineIdx, sectionIdx, companyIdx) => {
    setEngineGroups(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const company = updated[groupIdx].engines[engineIdx].sections[sectionIdx].companies[companyIdx];
      company.active = !company.active;
      if (!company.active) {
        company.products = company.products.map(p => ({ ...p, active: false }));
      }
      return updated;
    });
    api.post('/engines/update-product', { field: 'company_active' }).catch(() => {});
  };

  const toggleProduct = (groupIdx, engineIdx, sectionIdx, companyIdx, productIdx) => {
    setEngineGroups(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const product = updated[groupIdx].engines[engineIdx].sections[sectionIdx].companies[companyIdx].products[productIdx];
      product.active = !product.active;
      return updated;
    });
    api.post('/engines/update-product', { field: 'product_active' }).catch(() => {});
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const isCompanyVisible = (company) => {
    if (!searchText) return true;
    const q = searchText.toLowerCase();
    if (company.name.toLowerCase().includes(q)) return true;
    return company.products?.some(p => p.name.toLowerCase().includes(q));
  };

  const subdomain = brand?.subdomain_alias || brand?.subdomain || 'demo';

  if (loading) {
    return (
      <ProtectedLayout>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 16px' }}>
          {[1,2,3].map(i => <div key={i} className="skeleton" style={{ height: '80px', marginBottom: '12px', borderRadius: '12px' }}></div>)}
        </div>
      </ProtectedLayout>
    );
  }

  return (
    <ProtectedLayout>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 16px' }}>
        <h1 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '24px', color: '#0F172A' }}>Manage Engines</h1>

        {/* Engine Group Cards */}
        {engineGroups.map((group, groupIdx) => (
          <div key={group.title} style={{ marginBottom: '32px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '20px' }}>
              {group.engines.map((engine, engineIdx) => (
                <div key={engine.id} className="portal-card" style={{ overflow: 'hidden' }}>
                  <div style={{ padding: '16px 20px', fontWeight: '600', fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #DDE7F1' }}>
                    <span>{engine.title}</span>
                    <Toggle active={engine.active} onChange={() => toggleEngine(groupIdx, engineIdx)} />
                  </div>
                  <div style={{ padding: '12px 20px' }}>
                    {engine.sections.map((section) => (
                      section.companies.map((company) => (
                        company.active ? (
                          <div key={company.id} style={{ fontSize: '13px', color: '#334155', padding: '3px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <i className="fas fa-check-circle text-green-400 text-xs"></i>{company.name}
                          </div>
                        ) : null
                      ))
                    ))}
                    <a
                      href={`https://${subdomain}.quoteplicity.com/quote/${engine.url_slug}`}
                      target="_blank" rel="noreferrer"
                      style={{ fontSize: '12px', color: '#2D87F0', textDecoration: 'none', display: 'inline-block', marginTop: '8px' }}
                    >
                      Preview <i className="fas fa-external-link-alt text-xs"></i>
                    </a>
                  </div>
                </div>
              ))}
            </div>

            {/* Product sections for each engine */}
            {group.engines.map((engine, engineIdx) =>
              engine.active ? (
                engine.sections.map((section, sectionIdx) => (
                  <div key={section.id} className="portal-card" style={{ overflow: 'hidden', marginBottom: '12px' }}>
                    {/* Section header */}
                    <div
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', cursor: 'pointer' }}
                      onClick={() => toggleSection(section.id)}
                    >
                      <span style={{ fontWeight: '600', fontSize: '15px' }}>
                        {engine.title}{engine.sections.length > 1 ? ` — ${section.title}` : ''}
                      </span>
                      <i className={`fas fa-chevron-${expandedSections[section.id] ? 'up' : 'down'} text-slate-400`}></i>
                    </div>

                    {expandedSections[section.id] && (
                      <div style={{ borderTop: '1px solid #DDE7F1', padding: '16px 20px' }}>
                        {/* Search */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                          <input
                            type="text" className="input-field" style={{ width: '260px' }}
                            placeholder="Start typing to filter..."
                            value={searchText} onChange={e => setSearchText(e.target.value)}
                          />
                        </div>

                        {/* Company grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                          {section.companies.filter(isCompanyVisible).map((company, companyIdx) => (
                            <div key={company.id} className="portal-card" style={{ overflow: 'hidden', boxShadow: 'none' }}>
                              <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #DDE7F1' }}>
                                <span style={{ fontWeight: '600', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{company.name}</span>
                                <Toggle
                                  active={company.active}
                                  onChange={() => toggleCompany(groupIdx, engineIdx, sectionIdx, section.companies.findIndex(c => c.id === company.id))}
                                />
                              </div>
                              {company.active && (
                                <div style={{ padding: '8px 14px' }}>
                                  {company.products.map((product, productIdx) => (
                                    <div key={product.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #F8FAFC' }}>
                                      <span style={{ fontSize: '12px', color: product.active ? '#334155' : '#94A3B8', flex: 1 }}>{product.name}</span>
                                      <Toggle
                                        active={product.active}
                                        onChange={() => toggleProduct(groupIdx, engineIdx, sectionIdx, section.companies.findIndex(c => c.id === company.id), productIdx)}
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              ) : null
            )}
          </div>
        ))}
      </div>
    </ProtectedLayout>
  );
}
